import { useState } from 'react'
import { useLang } from '../lib/LangContext'

const sections = (lang) => [
  {
    id: 'debut',
    icon: '🚀',
    titre: lang === 'fr' ? 'Bien démarrer' : 'Getting started',
    questions: [
      {
        q: lang === 'fr' ? 'Comment ajouter mon premier produit ?' : 'How do I add my first product?',
        r: lang === 'fr'
          ? `Clique sur "Ajouter" dans la barre du bas.\n\n✅ Entre le nom exact du produit\n✅ La quantité actuelle en stock\n✅ L'unité de vente (ce que tu vends au client)\n✅ Le prix d'achat (ce que tu paies au fournisseur)\n✅ Le prix de vente (ce que le client te paie)\n✅ Le seuil d'alerte (quand te prévenir que le stock est faible)\n\n💡 Conseil : L'unité doit correspondre à ce que tu vends réellement. Si tu vends du riz à la mesure, l'unité = "mesure". Si tu vends des bouteilles entières, l'unité = "bouteille".`
          : `Click "Add" in the bottom bar.\n\n✅ Enter the exact product name\n✅ Current stock quantity\n✅ Sales unit (what you sell to the customer)\n✅ Purchase price (what you pay the supplier)\n✅ Selling price (what the customer pays you)\n✅ Alert threshold (when to warn you stock is low)\n\n💡 Tip: The unit must match what you actually sell. If you sell rice by the cup, unit = "cup".`
      },
      {
        q: lang === 'fr' ? 'Gros ou détail — comment entrer mes produits ?' : 'Bulk or detail — how should I enter products?',
        r: lang === 'fr'
          ? `👉 Toujours entrer en DÉTAIL — c'est-à-dire dans l'unité que tu vends au client.\n\n❌ MAUVAIS exemple :\nProduit : Riz | Unité : sac de 50kg | Prix : 25 000 FCFA\n→ Problème : si tu vends à la mesure, chaque sortie = 1 sac entier vendu, ce qui est faux.\n\n✅ BON exemple :\nProduit : Riz | Unité : mesure | Prix : 500 FCFA\n→ Chaque sortie = 1 mesure vendue = bilan exact.\n\nAutres exemples :\n• Œufs → unité : œuf | prix : 150 FCFA\n• Huile → unité : bouteille | prix : 1 500 FCFA\n• Savon → unité : plaquette | prix : 300 FCFA\n• Sucre → unité : kg | prix : 700 FCFA`
          : `👉 Always enter in DETAIL — meaning the unit you sell to the customer.\n\n❌ BAD example:\nProduct: Rice | Unit: 50kg bag | Price: 25,000 FCFA\n→ Problem: if you sell by the cup, each exit = 1 whole bag sold, which is wrong.\n\n✅ GOOD example:\nProduct: Rice | Unit: cup | Price: 500 FCFA\n→ Each exit = 1 cup sold = accurate report.\n\nOther examples:\n• Eggs → unit: egg | price: 150 FCFA\n• Oil → unit: bottle | price: 1,500 FCFA\n• Soap → unit: bar | price: 300 FCFA`
      },
    ]
  },
  {
    id: 'stock',
    icon: '📦',
    titre: lang === 'fr' ? 'Gérer le stock' : 'Managing stock',
    questions: [
      {
        q: lang === 'fr' ? 'Quand faire une Entrée ?' : 'When to record an Entry?',
        r: lang === 'fr'
          ? `Fais une ENTRÉE chaque fois que tu reçois de la marchandise.\n\n📦 Exemples :\n• Tu achètes 100 mesures de riz chez le grossiste → Entrée de 100\n• Tu reçois 50 bouteilles d'huile → Entrée de 50\n• Tu réapprovisionnes ton stock de savon → Entrée\n\n💡 Chaque entrée est comptée dans "J'ai dépensé" dans le bilan.`
          : `Record an ENTRY every time you receive merchandise.\n\n📦 Examples:\n• You buy 100 cups of rice from the wholesaler → Entry of 100\n• You receive 50 bottles of oil → Entry of 50\n• You restock your soap supply → Entry\n\n💡 Each entry is counted in "I spent" in the report.`
      },
      {
        q: lang === 'fr' ? 'Quand faire une Sortie ?' : 'When to record an Exit?',
        r: lang === 'fr'
          ? `Fais une SORTIE chaque fois que tu vends quelque chose.\n\n🛒 Exemples :\n• Un client achète 3 mesures de riz → Sortie de 3\n• Tu vends 2 bouteilles d'huile → Sortie de 2\n• Un client prend 5 œufs → Sortie de 5\n\n💡 Chaque sortie est comptée dans "J'ai encaissé" dans le bilan.\n\n⚡ Astuce : Pour aller plus vite, le caissier peut faire les sorties depuis son téléphone avec le code PIN.`
          : `Record an EXIT every time you make a sale.\n\n🛒 Examples:\n• A customer buys 3 cups of rice → Exit of 3\n• You sell 2 bottles of oil → Exit of 2\n• A customer takes 5 eggs → Exit of 5\n\n💡 Each exit is counted in "I received" in the report.`
      },
      {
        q: lang === 'fr' ? 'Comment modifier un produit ?' : 'How to edit a product?',
        r: lang === 'fr'
          ? `Clique directement sur le produit dans la liste "Mon Stock".\n\nUne fiche s'ouvre où tu peux modifier :\n✏️ Le nom\n💰 Le prix d'achat et de vente\n📦 L'unité\n⚠️ Le seuil d'alerte\n🏷️ La catégorie\n\n💡 Mets à jour les prix dès qu'ils changent chez le fournisseur pour que ton bilan reste précis.`
          : `Click directly on the product in the "My Stock" list.\n\nA form opens where you can edit:\n✏️ Name\n💰 Purchase and selling price\n📦 Unit\n⚠️ Alert threshold\n🏷️ Category\n\n💡 Update prices as soon as they change at the supplier to keep your report accurate.`
      },
      {
        q: lang === 'fr' ? 'J\'ai fait une erreur — comment annuler ?' : 'I made a mistake — how to cancel?',
        r: lang === 'fr'
          ? `Va dans "Historique" → tu vois tous tes mouvements.\n\nSi le mouvement a moins de 24h, un bouton "↩ Annuler" apparaît à droite.\n\nClique dessus → confirme → le stock revient automatiquement à l'état précédent.\n\n⚠️ L'annulation crée un mouvement inverse dans l'historique pour garder une trace.`
          : `Go to "History" → you see all your movements.\n\nIf the movement is less than 24h old, an "↩ Undo" button appears on the right.\n\nClick it → confirm → stock automatically returns to previous state.\n\n⚠️ The cancellation creates a reverse movement in history to keep a trace.`
      },
    ]
  },
  {
    id: 'bilan',
    icon: '📊',
    titre: lang === 'fr' ? 'Comprendre le bilan' : 'Understanding the report',
    questions: [
      {
        q: lang === 'fr' ? 'Que signifient les 4 cartes du bilan ?' : 'What do the 4 report cards mean?',
        r: lang === 'fr'
          ? `💰 J'AI ENCAISSÉ\nTotal des ventes au prix de vente.\nEx: tu as vendu 50 mesures de riz à 500 FCFA = 25 000 FCFA encaissés.\n\n🛒 J'AI DÉPENSÉ\nTotal des achats au prix d'achat fournisseur.\nEx: tu as reçu 100 mesures à 350 FCFA = 35 000 FCFA dépensés.\n\n📦 MON STOCK VAUT\nValeur de tout ton stock au prix de vente.\nEx: il te reste 70 mesures à 500 FCFA = 35 000 FCFA de stock.\n\n📈 MON BÉNÉFICE BRUT\nCe que tu as réellement gagné = Encaissé − Coût des produits vendus.\nEx: tu vends 1 mesure à 500 FCFA, tu l'avais achetée à 350 FCFA → bénéfice = 150 FCFA par mesure.`
          : `💰 I RECEIVED\nTotal sales at selling price.\n\n🛒 I SPENT\nTotal purchases at supplier purchase price.\n\n📦 MY STOCK IS WORTH\nValue of all your stock at selling price.\n\n📈 MY GROSS PROFIT\nWhat you actually earned = Received − Cost of goods sold.`
      },
      {
        q: lang === 'fr' ? 'Pourquoi mon bénéfice est négatif ?' : 'Why is my profit negative?',
        r: lang === 'fr'
          ? `Un bénéfice négatif peut avoir plusieurs causes :\n\n1️⃣ Tu as acheté beaucoup de stock ce mois mais pas encore tout vendu → normal, ça s'équilibrera.\n\n2️⃣ Ton prix de vente est inférieur ou égal au prix d'achat → vérifie tes prix.\n\n3️⃣ Le prix d'achat n'est pas renseigné sur certains produits → clique sur le produit pour le corriger.\n\n💡 Si le bilan affiche "-" mais que ta caisse est positive, c'est souvent le cas n°1 : ton stock vaut de l'argent qui sera gagné quand tu vends.`
          : `A negative profit can have several causes:\n\n1️⃣ You bought a lot of stock this month but haven't sold it all yet → normal, it will balance out.\n\n2️⃣ Your selling price is equal to or less than purchase price → check your prices.\n\n3️⃣ Purchase price is not set for some products → click the product to fix it.`
      },
    ]
  },
  {
    id: 'previsions',
    icon: '🔮',
    titre: lang === 'fr' ? 'Prévisions & conseils' : 'Forecasts & tips',
    questions: [
      {
        q: lang === 'fr' ? 'Comment fonctionnent les alertes de stock ?' : 'How do stock alerts work?',
        r: lang === 'fr'
          ? `StockO surveille ton stock en permanence et t'alerte selon 3 niveaux :\n\n🚨 CRITIQUE : Stock épuisé ou rupture dans moins de 3 jours → commander d'urgence\n⚠️ URGENT : Rupture dans moins de 7 jours → commander bientôt\n👁 À SURVEILLER : Rupture dans moins de 14 jours → garder un œil\n\nLe seuil d'alerte se configure à l'ajout du produit. Par défaut il est à 5 unités.`
          : `StockO monitors your stock continuously and alerts you at 3 levels:\n\n🚨 CRITICAL: Out of stock or stockout in less than 3 days → order urgently\n⚠️ URGENT: Stockout in less than 7 days → order soon\n👁 WATCH: Stockout in less than 14 days → keep an eye on it`
      },
      {
        q: lang === 'fr' ? 'Comment fonctionnent les conseils intelligents ?' : 'How do smart tips work?',
        r: lang === 'fr'
          ? `Chaque semaine, StockO analyse tes 30 derniers jours et te donne des conseils basés sur :\n\n📈 La vitesse de vente de chaque produit (combien tu en vends par jour)\n💰 Le bénéfice par unité (combien tu gagnes sur chaque vente)\n\nExemple de conseil :\n"🌟 Augmente ton stock de Riz — il se vend 3 mesures/jour et te rapporte 150 FCFA par mesure. Passer de 50 à 100 mesures pourrait t'apporter +3 150 FCFA de bénéfice de plus cette semaine."\n\n💡 Plus tu enregistres tes mouvements régulièrement, plus les conseils sont précis.`
          : `Each week, StockO analyzes your last 30 days and gives advice based on:\n\n📈 Sales speed of each product (how many you sell per day)\n💰 Profit per unit (how much you earn on each sale)\n\n💡 The more regularly you record movements, the more accurate the tips.`
      },
    ]
  },
  {
    id: 'caissier',
    icon: '🧑‍💼',
    titre: lang === 'fr' ? 'Mode caissier' : 'Cashier mode',
    questions: [
      {
        q: lang === 'fr' ? 'Comment donner accès à mon caissier ?' : 'How to give my cashier access?',
        r: lang === 'fr'
          ? `Va dans ☰ → Mon équipe → active le code caissier.\n\nTon caissier aura :\n• Un code boutique (4 lettres) unique à ta boutique\n• Un code PIN (4 chiffres) que tu définis\n\nIl se connecte sur l'app avec ces 2 codes — sans avoir besoin de ton email ou mot de passe.\n\n🔒 Le caissier voit : Stock, Ajouter, Historique, Prévisions\n🚫 Il ne voit PAS : Bilan, Abonnement, Paramètres`
          : `Go to ☰ → My team → activate the cashier code.\n\nYour cashier will have:\n• A shop code (4 letters) unique to your shop\n• A PIN code (4 digits) that you set\n\nThey log in with these 2 codes — no need for your email or password.`
      },
      {
        q: lang === 'fr' ? 'Le caissier peut-il voir le bilan ?' : 'Can the cashier see the report?',
        r: lang === 'fr'
          ? `Non. Le caissier a accès uniquement aux fonctions opérationnelles :\n✅ Voir le stock\n✅ Ajouter des produits\n✅ Enregistrer des entrées et sorties\n✅ Voir l'historique\n✅ Voir les prévisions\n\n🚫 Il ne peut pas voir le bilan financier, l'abonnement ou les paramètres.\n\nSeul le patron (toi) voit les chiffres financiers.`
          : `No. The cashier only has access to operational functions:\n✅ View stock\n✅ Add products\n✅ Record entries and exits\n✅ View history\n✅ View forecasts\n\n🚫 Cannot see financial report, subscription or settings.`
      },
    ]
  },
  {
    id: 'astuces',
    icon: '💡',
    titre: lang === 'fr' ? 'Astuces & bonnes pratiques' : 'Tips & best practices',
    questions: [
      {
        q: lang === 'fr' ? 'Comment avoir un bilan vraiment précis ?' : 'How to have a truly accurate report?',
        r: lang === 'fr'
          ? `5 règles d'or pour un bilan précis :\n\n1️⃣ Entre toujours les prix d'achat ET de vente pour chaque produit\n2️⃣ Enregistre chaque vente au moment où elle se fait (ou à la fin de la journée)\n3️⃣ Enregistre chaque réapprovisionnement dès réception\n4️⃣ Utilise l'unité que tu vends au client (pas en gros)\n5️⃣ Mets à jour les prix dès qu'ils changent chez le fournisseur\n\n⚡ Astuce pro : Confie les sorties à ton caissier pour que tout soit enregistré en temps réel.`
          : `5 golden rules for an accurate report:\n\n1️⃣ Always enter purchase AND selling price for each product\n2️⃣ Record each sale as it happens (or at end of day)\n3️⃣ Record each restock upon receipt\n4️⃣ Use the unit you sell to the customer (not bulk)\n5️⃣ Update prices as soon as they change at supplier`
      },
      {
        q: lang === 'fr' ? 'L\'app fonctionne-t-elle sans internet ?' : 'Does the app work without internet?',
        r: lang === 'fr'
          ? `Oui ! StockO fonctionne en mode hors-ligne.\n\n📱 Sans connexion :\n✅ Tu peux consulter ton stock\n✅ Tu peux enregistrer des mouvements\n✅ Les données sont sauvegardées localement\n\n🔄 Quand tu te reconnectes :\n→ Tout se synchronise automatiquement avec le serveur.\n\n💡 Installe StockO sur ton téléphone (bouton "Ajouter à l'écran d'accueil" dans ton navigateur) pour un accès rapide même sans connexion.`
          : `Yes! StockO works in offline mode.\n\n📱 Without connection:\n✅ You can view your stock\n✅ You can record movements\n✅ Data is saved locally\n\n🔄 When you reconnect:\n→ Everything syncs automatically with the server.`
      },
      {
        q: lang === 'fr' ? 'Comment installer StockO sur mon téléphone ?' : 'How to install StockO on my phone?',
        r: lang === 'fr'
          ? `StockO est une PWA — tu peux l'installer comme une vraie app !\n\n📱 Sur Android (Chrome) :\n1. Ouvre stocko-zeta.vercel.app dans Chrome\n2. Clique sur les 3 points ⋮ en haut à droite\n3. Clique "Ajouter à l'écran d'accueil"\n4. Confirme → StockO apparaît comme une app sur ton téléphone !\n\n🍎 Sur iPhone (Safari) :\n1. Ouvre l'app dans Safari\n2. Clique sur le bouton Partager 📤\n3. Clique "Sur l'écran d'accueil"\n4. Confirme`
          : `StockO is a PWA — you can install it like a real app!\n\n📱 On Android (Chrome):\n1. Open stocko-zeta.vercel.app in Chrome\n2. Click the 3 dots ⋮ at top right\n3. Click "Add to home screen"\n4. Confirm → StockO appears as an app!\n\n🍎 On iPhone (Safari):\n1. Open the app in Safari\n2. Click the Share button 📤\n3. Click "Add to Home Screen"\n4. Confirm`
      },
    ]
  },
]

export default function Aide() {
  const { lang } = useLang()
  const [ouvert, setOuvert] = useState({})
  const [sectionActive, setSectionActive] = useState(null)

  function toggleQ(id) {
    setOuvert(o => ({ ...o, [id]: !o[id] }))
  }

  function toggleSection(id) {
    setSectionActive(s => s === id ? null : id)
  }

  const data = sections(lang)

  return (
    <div className="main">
      <div className="page-title">
        {lang === 'fr' ? '📖 Centre d\'aide' : '📖 Help Center'}
      </div>

      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: '1.5rem', lineHeight: 1.7 }}>
        {lang === 'fr'
          ? 'Tout ce que tu dois savoir pour bien utiliser StockO.'
          : 'Everything you need to know to use StockO properly.'}
      </div>

      {data.map(section => (
        <div key={section.id} style={{ marginBottom: 10 }}>
          {/* EN-TÊTE SECTION */}
          <button onClick={() => toggleSection(section.id)} style={{
            width: '100%', padding: '14px 16px', borderRadius: sectionActive === section.id ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)',
            background: sectionActive === section.id ? 'var(--sage-pale)' : 'var(--warm-white)',
            border: `1.5px solid ${sectionActive === section.id ? 'var(--sage-light)' : 'var(--cream-dark)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{section.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: sectionActive === section.id ? 'var(--sage-dark)' : 'var(--charcoal)' }}>
                {section.titre}
              </span>
            </div>
            <span style={{ fontSize: 18, color: 'var(--muted)', transform: sectionActive === section.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              ›
            </span>
          </button>

          {/* QUESTIONS */}
          {sectionActive === section.id && (
            <div style={{ border: '1.5px solid var(--sage-light)', borderTop: 'none', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)', overflow: 'hidden' }}>
              {section.questions.map((item, idx) => {
                const qId = `${section.id}-${idx}`
                return (
                  <div key={qId}>
                    {/* QUESTION */}
                    <button onClick={() => toggleQ(qId)} style={{
                      width: '100%', padding: '12px 16px', background: 'var(--warm-white)',
                      border: 'none', borderTop: idx > 0 ? '1px solid var(--cream-dark)' : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textAlign: 'left', gap: 10
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--charcoal)', lineHeight: 1.4 }}>
                        {item.q}
                      </span>
                      <span style={{ fontSize: 16, color: 'var(--sage)', flexShrink: 0, transform: ouvert[qId] ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }}>
                        +
                      </span>
                    </button>

                    {/* RÉPONSE */}
                    {ouvert[qId] && (
                      <div style={{
                        padding: '12px 16px 16px', background: 'var(--cream)',
                        borderTop: '1px solid var(--cream-dark)',
                        fontSize: 12, color: 'var(--charcoal)', lineHeight: 1.8,
                        whiteSpace: 'pre-line'
                      }}>
                        {item.r}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ))}

      {/* CONTACT SUPPORT */}
      <div style={{ background: 'var(--sage-pale)', border: '1px solid var(--sage-light)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginTop: '1rem', textAlign: 'center' }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>🤝</div>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 15, color: 'var(--sage-dark)', marginBottom: 6 }}>
          {lang === 'fr' ? 'Tu as encore une question ?' : 'Still have a question?'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
          {lang === 'fr' ? 'Contacte-nous sur WhatsApp :' : 'Contact us on WhatsApp:'}
        </div>
        <a href="https://wa.me/237698366615" target="_blank" rel="noreferrer" style={{
          display: 'inline-block', marginTop: 10, padding: '10px 20px',
          background: '#25D366', color: 'white', borderRadius: 50,
          fontSize: 13, fontWeight: 600, textDecoration: 'none', fontFamily: 'DM Sans, sans-serif'
        }}>
          💬 WhatsApp Support
        </a>
      </div>
    </div>
  )
}
